import { action, computed, makeObservable, observable } from 'mobx';
import { NodeBaseModel } from '..';
import {
    ContractsIngestionRuntimeInputDto,
    RequiredConditionDto,
    DataType,
    InputMetadataEntry,
    RequiredOverride
} from '../../types';
import { TypeUtils } from '../../../common/misc';
import { Utils } from '../../../custom_shared/misc';
import { InputMetadata, NodeIds, InputIds, ChildInputMetadata } from '../../misc';

const getInputMetadata = (nodeId: string, inputId: string): InputMetadataEntry | undefined => {
    if (inputId.startsWith(InputIds.Limits.LimitType)) {
        return InputMetadata[NodeIds.Limits][InputIds.Limits.LimitType];
    }

    if (inputId.startsWith(InputIds.Limits.LimitValue)) {
        return InputMetadata[NodeIds.Limits][InputIds.Limits.LimitValue];
    }

    if (inputId.startsWith(InputIds.Deductions.DeductionType)) {
        return InputMetadata[NodeIds.Deductions][InputIds.Deductions.DeductionType];
    }

    if (inputId.startsWith(InputIds.Deductions.DeductionValue)) {
        return InputMetadata[NodeIds.Deductions][InputIds.Deductions.DeductionValue];
    }

    const nodeMetadata =
        ChildInputMetadata[nodeId as keyof typeof ChildInputMetadata] ??
        InputMetadata[nodeId as keyof typeof InputMetadata];

    if (!nodeMetadata) {
        return undefined;
    }

    return nodeMetadata[inputId as keyof typeof nodeMetadata];
};

export default abstract class InputBaseModel {
    readonly id: string;
    readonly name: string;
    readonly guid: string;
    readonly parentInputId: string | null;
    readonly initialValue: unknown;
    readonly meta: Record<string, unknown>;
    readonly sourceId: string | null;
    readonly dataType: DataType;
    readonly requiredConditions: RequiredConditionDto[] | null;
    requiredOverride: RequiredOverride | null;

    value: unknown;
    required: boolean;
    isReadOnly: boolean = false;
    isControlsHidden: boolean = false;
    hasUnsavedChanges: boolean = false;

    constructor(
        private readonly node: NodeBaseModel,
        data: ContractsIngestionRuntimeInputDto
    ) {
        const inputMetadata = getInputMetadata(
            node.id as keyof typeof InputMetadata,
            data.id as keyof (typeof InputMetadata)[keyof typeof InputMetadata]
        );

        this.id = data.id;
        this.name = inputMetadata?.name ?? data.id;
        this.guid = data.guid;
        this.parentInputId = data.parentInputId;
        this.value = this.getValue(data.value);
        this.initialValue = this.value;
        this.meta = data.meta;
        this.sourceId = data.sourceId;
        this.dataType = data.dataType;
        this.required = data.required;
        this.requiredOverride = data.requiredOverride ?? null;
        this.requiredConditions = data.requiredConditions;

        makeObservable(this, {
            value: observable,
            required: observable,
            isReadOnly: observable,
            isControlsHidden: observable,
            hasUnsavedChanges: observable,
            setValue: action.bound,
            setRequired: action.bound,
            setIsReadOnly: action.bound,
            setIsControlsHidden: action.bound,
            setHasUnsavedChanges: action.bound,
            formattedValue: computed,
            inputsAtSameLevel: computed,
            parentInput: computed,
            childInput: computed,
            hasValue: computed
        });
    }

    setValue(value: unknown) {
        this.value = this.getValue(value);
        this.node.inputValueChangeSubject.next(this);
    }

    setRequired(required: boolean) {
        this.required = required;
    }

    setRequiredOverride(requiredOverride: RequiredOverride | null) {
        this.requiredOverride = requiredOverride;
    }

    setIsReadOnly(isReadOnly: boolean) {
        this.isReadOnly = isReadOnly;
    }

    setIsControlsHidden(isControlsHidden: boolean) {
        this.isControlsHidden = isControlsHidden;
    }

    setHasUnsavedChanges(hasUnsavedChanges: boolean) {
        this.hasUnsavedChanges = hasUnsavedChanges;
    }

    validate() {
        this.node.inputValidationSubject.next(this);
    }

    abstract get formattedValue(): unknown;

    get inputsAtSameLevel() {
        return this.node.getInputsAtSameLevel(this.guid);
    }

    get hasValue() {
        if (TypeUtils.isString(this.value) || TypeUtils.isArray(this.value)) {
            return this.value.length > 0;
        }

        return this.value !== null;
    }

    get parentInput() {
        return this.inputsAtSameLevel.find(i => i.id === this.parentInputId);
    }

    get childInput() {
        return this.inputsAtSameLevel.find(i => i.parentInputId === this.id);
    }

    get dto(): ContractsIngestionRuntimeInputDto {
        return {
            id: this.id,
            guid: this.guid,
            parentInputId: this.parentInputId,
            value:
                this.dataType === 'DateTime'
                    ? Utils.convertDateValueToString(this.value, { applyStartOfDay: true })
                    : this.value,
            meta: this.meta,
            sourceId: this.sourceId,
            dataType: this.dataType,
            required: this.required,
            requiredOverride: this.requiredOverride,
            requiredConditions: this.requiredConditions
        };
    }

    update(data: ContractsIngestionRuntimeInputDto) {
        this.setValue(data.value);
        this.setRequiredOverride(data.requiredOverride ?? null);
        this.setRequired(data.required);
    }

    private getValue(value: unknown) {
        return value === undefined ? null : value;
    }
}
